import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import ClearOutlined from "@mui/icons-material/ClearOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";

const ENTER_KEY_CODE = 13;
const DEFAULT_LABEL_PLACEHOLDER = "Click To Edit";

// Azure ID (UUID) format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
const AZURE_ID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EditableFieldAzureId = ({
  field,
  initialValue,
  tip,
  required,
  onChange = () => {},
  setIsUpdating,
  ...props
}) => {
  const [isEditing, setEditing] = useState(false);
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");

  // Split UUID into 5 segments: 8-4-4-4-12 characters
  const parseUUID = (uuid) => {
    if (!uuid) return ["", "", "", "", ""];
    const cleaned = uuid.replace(/-/g, "");
    return [
      cleaned.slice(0, 8),
      cleaned.slice(8, 12),
      cleaned.slice(12, 16),
      cleaned.slice(16, 20),
      cleaned.slice(20, 32),
    ];
  };

  const [segments, setSegments] = useState(parseUUID(initialValue));

  // Refs for auto-focus between boxes
  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  useEffect(() => {
    setSegments(parseUUID(initialValue));
  }, [initialValue]);

  const buildUUID = (segs) => {
    return `${segs[0]}-${segs[1]}-${segs[2]}-${segs[3]}-${segs[4]}`;
  };

  const validateAzureId = (segs) => {
    const uuid = buildUUID(segs);

    // Check if all segments are filled
    const allFilled = segs[0].length === 8 &&
                      segs[1].length === 4 &&
                      segs[2].length === 4 &&
                      segs[3].length === 4 &&
                      segs[4].length === 12;

    if (!allFilled && !required) {
      // Check if completely empty
      const allEmpty = segs.every(s => s.length === 0);
      if (allEmpty) {
        setError(false);
        setHelperText("");
        return true;
      }
    }

    if (!allFilled && required) {
      setError(true);
      setHelperText("All segments must be filled");
      return false;
    }

    if (allFilled && !AZURE_ID_REGEX.test(uuid)) {
      setError(true);
      setHelperText("Invalid Azure ID format");
      return false;
    }

    setError(false);
    setHelperText("");
    return true;
  };

  const handleSegmentChange = (index, value) => {
    // Only allow hex characters
    const hexValue = value.toLowerCase().replace(/[^0-9a-f]/g, "");

    // Max lengths for each segment
    const maxLengths = [8, 4, 4, 4, 12];
    const truncatedValue = hexValue.slice(0, maxLengths[index]);

    const newSegments = [...segments];
    newSegments[index] = truncatedValue;
    setSegments(newSegments);
    validateAzureId(newSegments);

    // Auto-focus to next input when segment is complete
    if (truncatedValue.length === maxLengths[index] && index < 4) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.keyCode === ENTER_KEY_CODE) {
      handleSave();
    } else if (e.key === "Backspace" && segments[index].length === 0 && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs[index - 1].current?.focus();
    } else if (e.key === "ArrowLeft" && e.target.selectionStart === 0 && index > 0) {
      inputRefs[index - 1].current?.focus();
    } else if (e.key === "ArrowRight" && e.target.selectionStart === segments[index].length && index < 4) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").replace(/-/g, "").toLowerCase();

    // Only allow hex characters
    const hexText = pastedText.replace(/[^0-9a-f]/g, "");

    const newSegments = [
      hexText.slice(0, 8),
      hexText.slice(8, 12),
      hexText.slice(12, 16),
      hexText.slice(16, 20),
      hexText.slice(20, 32),
    ];

    setSegments(newSegments);
    validateAzureId(newSegments);
  };

  const handleSave = () => {
    if (validateAzureId(segments)) {
      const uuid = buildUUID(segments);
      setEditing(false);
      if (uuid !== initialValue) {
        onChange(uuid);
        if (setIsUpdating) {
          setIsUpdating(true);
        }
      }
    }
  };

  const handleCancel = () => {
    setSegments(parseUUID(initialValue));
    setError(false);
    setHelperText("");
    setEditing(false);
  };

  const handleEdit = () => {
    setEditing(true);
    // Focus first input after render
    setTimeout(() => {
      inputRefs[0].current?.focus();
    }, 0);
  };

  const currentValue = buildUUID(segments);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {isEditing ? (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {segments.map((segment, index) => (
              <React.Fragment key={index}>
                <TextField
                  inputRef={inputRefs[index]}
                  value={segment}
                  onChange={(e) => handleSegmentChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  error={error}
                  placeholder={index === 0 ? "xxxxxxxx" : index === 4 ? "xxxxxxxxxxxx" : "xxxx"}
                  autoFocus={index === 0}
                  inputProps={{
                    style: {
                      fontFamily: "monospace",
                      textAlign: "center",
                      padding: "8px 4px",
                      textTransform: "lowercase",
                    },
                    maxLength: index === 0 ? 8 : index === 4 ? 12 : 4,
                  }}
                  sx={{
                    width: index === 0 ? "120px" : index === 4 ? "180px" : "70px",
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "monospace",
                    },
                  }}
                />
                {index < 4 && (
                  <Typography
                    variant="h6"
                    sx={{ color: "text.secondary", userSelect: "none" }}
                  >
                    -
                  </Typography>
                )}
              </React.Fragment>
            ))}
            <IconButton
              onClick={handleSave}
              disabled={error}
              size="small"
              aria-label="Save"
              color="primary"
              sx={{ ml: 1 }}
            >
              <CheckOutlined />
            </IconButton>
            <IconButton
              onClick={handleCancel}
              size="small"
              aria-label="Cancel"
              color="secondary"
            >
              <ClearOutlined />
            </IconButton>
          </Box>
          {helperText && (
            <Typography
              variant="caption"
              sx={{ color: error ? "error.main" : "text.secondary", mt: 0.5 }}
            >
              {helperText}
            </Typography>
          )}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "action.hover",
            },
            padding: "8px",
            borderRadius: "4px",
          }}
          onClick={handleEdit}
        >
          <Tooltip title={tip || "Click to edit"}>
            <Typography
              variant="body1"
              sx={{
                flexGrow: 1,
                fontFamily: "monospace",
                color: currentValue && currentValue !== "----" ? "text.primary" : "text.disabled",
              }}
            >
              {currentValue && currentValue !== "----" ? currentValue : DEFAULT_LABEL_PLACEHOLDER}
            </Typography>
          </Tooltip>
          <IconButton size="small" aria-label="Edit" onClick={handleEdit}>
            <EditOutlined fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

EditableFieldAzureId.propTypes = {
  field: PropTypes.string,
  initialValue: PropTypes.string,
  tip: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  setIsUpdating: PropTypes.func,
};

EditableFieldAzureId.defaultProps = {
  required: false,
};

export default EditableFieldAzureId;
